import { AIModule } from '../types';

export const SMART_IOT_SYSTEMS_MODULES: AIModule[] = [
  {
    id: 'smart-iot-systems',
    title: 'Smart IoT Systems',
    description: 'Build intelligent IoT systems with edge computing, sensor fusion, and distributed AI.',
    estimatedTime: '85 minutes',
    duration: '85 minutes',
    difficulty: 'intermediate' as const,
    content: `
# Smart IoT Systems with AI

## Overview
Smart IoT systems combine internet-connected devices with artificial intelligence to create autonomous, adaptive systems that can make intelligent decisions at the edge.

## IoT Architecture for AI

### 1. Edge-Cloud Hybrid Architecture

\`\`\`python
import asyncio
import json
import time
from typing import Dict, Any, List
import numpy as np
from datetime import datetime

class IoTEdgeNode:
    def __init__(self, node_id: str, capabilities: List[str]):
        self.node_id = node_id
        self.capabilities = capabilities
        self.local_models = {}
        self.sensor_data = {}
        self.edge_decisions = []
        
    async def collect_sensor_data(self, sensor_type: str, data: Dict[str, Any]):
        """Collect and preprocess sensor data"""
        timestamp = datetime.now().isoformat()
        
        # Basic preprocessing
        processed_data = {
            'timestamp': timestamp,
            'sensor_type': sensor_type,
            'raw_data': data,
            'processed_data': await self.preprocess_data(sensor_type, data)
        }
        
        self.sensor_data[sensor_type] = processed_data
        
        # Trigger local AI processing if model available
        if sensor_type in self.local_models:
            await self.process_locally(sensor_type, processed_data)
            
    async def preprocess_data(self, sensor_type: str, data: Dict[str, Any]):
        """Preprocess raw sensor data"""
        if sensor_type == 'temperature':
            return {
                'celsius': data.get('celsius', 0),
                'fahrenheit': data.get('celsius', 0) * 9/5 + 32,
                'anomaly_score': abs(data.get('celsius', 20) - 20) / 10  # Deviation from 20C
            }
        elif sensor_type == 'accelerometer':
            x, y, z = data.get('x', 0), data.get('y', 0), data.get('z', 0)
            magnitude = np.sqrt(x**2 + y**2 + z**2)
            return {
                'x': x, 'y': y, 'z': z,
                'magnitude': magnitude,
                'movement_detected': magnitude > 1.2  # Threshold for movement
            }
        elif sensor_type == 'camera':
            # Simulate basic image preprocessing
            return {
                'resolution': data.get('resolution', '640x480'),
                'brightness': data.get('brightness', 128),
                'objects_detected': data.get('objects', []),
                'motion_detected': data.get('motion', False)
            }
        return data
        
    async def process_locally(self, sensor_type: str, data: Dict[str, Any]):
        """Process data with local AI models"""
        model = self.local_models[sensor_type]
        
        if sensor_type == 'temperature':
            # Anomaly detection
            temp = data['processed_data']['celsius']
            if temp > 35 or temp < 10:  # Extreme temperatures
                decision = {
                    'type': 'alert',
                    'message': f"Temperature anomaly detected: {temp}°C",
                    'action': 'send_to_cloud',
                    'priority': 'high'
                }
                await self.make_edge_decision(decision)
                
        elif sensor_type == 'accelerometer':
            # Movement pattern analysis
            if data['processed_data']['movement_detected']:
                decision = {
                    'type': 'activity',
                    'message': "Movement detected",
                    'action': 'log_locally',
                    'priority': 'low'
                }
                await self.make_edge_decision(decision)
                
        elif sensor_type == 'camera':
            # Object detection and security
            objects = data['processed_data']['objects_detected']
            if 'person' in objects and time.hour < 6 or time.hour > 22:  # After hours
                decision = {
                    'type': 'security',
                    'message': "Person detected after hours",
                    'action': 'alert_security',
                    'priority': 'critical'
                }
                await self.make_edge_decision(decision)
                
    async def make_edge_decision(self, decision: Dict[str, Any]):
        """Make intelligent decisions at the edge"""
        decision['timestamp'] = datetime.now().isoformat()
        decision['node_id'] = self.node_id
        
        self.edge_decisions.append(decision)
        
        # Route based on priority and capabilities
        if decision['priority'] == 'critical':
            await self.send_to_cloud_immediately(decision)
        elif decision['action'] == 'send_to_cloud':
            await self.queue_for_cloud(decision)
        else:
            await self.handle_locally(decision)
            
    async def send_to_cloud_immediately(self, decision: Dict[str, Any]):
        """Send critical decisions to cloud immediately"""
        print(f"CRITICAL: Sending to cloud: {decision['message']}")
        # In real implementation, this would use MQTT, HTTP, or other protocols
        
    async def queue_for_cloud(self, decision: Dict[str, Any]):
        """Queue non-critical data for batch upload"""
        print(f"QUEUED: {decision['message']}")
        
    async def handle_locally(self, decision: Dict[str, Any]):
        """Handle decisions that can be resolved locally"""
        print(f"LOCAL: {decision['message']}")

class SmartHomeSystem:
    def __init__(self):
        self.nodes = {}
        self.central_intelligence = CentralIntelligence()
        
    def add_node(self, node: IoTEdgeNode):
        """Add an IoT node to the system"""
        self.nodes[node.node_id] = node
        
    async def simulate_smart_home(self):
        """Simulate a smart home with multiple IoT devices"""
        # Create nodes
        thermostat = IoTEdgeNode("thermostat_01", ["temperature_control", "energy_optimization"])
        security_camera = IoTEdgeNode("camera_01", ["object_detection", "motion_analysis"])
        motion_sensor = IoTEdgeNode("motion_01", ["movement_detection", "occupancy_tracking"])
        
        # Add local AI models (simplified)
        thermostat.local_models['temperature'] = "temperature_anomaly_detector"
        security_camera.local_models['camera'] = "object_detection_model"
        motion_sensor.local_models['accelerometer'] = "movement_classifier"
        
        self.add_node(thermostat)
        self.add_node(security_camera)
        self.add_node(motion_sensor)
        
        # Simulate sensor data collection
        tasks = [
            thermostat.collect_sensor_data('temperature', {'celsius': 38}),  # High temp
            security_camera.collect_sensor_data('camera', {
                'objects': ['person', 'cat'], 
                'motion': True,
                'brightness': 45
            }),
            motion_sensor.collect_sensor_data('accelerometer', {
                'x': 0.1, 'y': 0.2, 'z': 1.1  # Slight movement
            })
        ]
        
        await asyncio.gather(*tasks)

class CentralIntelligence:
    """Cloud-based central intelligence for coordinating IoT systems"""
    
    def __init__(self):
        self.global_models = {}
        self.device_registry = {}
        self.system_state = {}
        
    def register_device(self, device_id: str, capabilities: List[str]):
        """Register a new IoT device"""
        self.device_registry[device_id] = {
            'capabilities': capabilities,
            'last_seen': datetime.now().isoformat(),
            'status': 'active'
        }
        
    def update_global_model(self, model_type: str, model_data: Dict[str, Any]):
        """Update global AI models based on federated learning"""
        if model_type not in self.global_models:
            self.global_models[model_type] = {
                'version': 1,
                'accuracy': 0.0,
                'last_updated': datetime.now().isoformat()
            }
        else:
            self.global_models[model_type]['version'] += 1
            self.global_models[model_type]['last_updated'] = datetime.now().isoformat()
            
    def optimize_system(self, system_data: Dict[str, Any]):
        """Optimize entire IoT system based on collected data"""
        optimization_recommendations = {
            'energy_savings': [],
            'security_improvements': [],
            'performance_tuning': []
        }
        
        # Analyze patterns and make recommendations
        if 'temperature_data' in system_data:
            temp_data = system_data['temperature_data']
            avg_temp = np.mean([d['celsius'] for d in temp_data])
            
            if avg_temp > 25:
                optimization_recommendations['energy_savings'].append({
                    'action': 'reduce_heating',
                    'potential_savings': '15%',
                    'priority': 'medium'
                })
                
        return optimization_recommendations
\`\`\`

### 2. Sensor Fusion and Multi-Modal AI

\`\`\`python
class SensorFusionSystem:
    def __init__(self):
        self.sensors = {}
        self.fusion_models = {}
        self.confidence_weights = {}
        
    def add_sensor(self, sensor_id: str, sensor_type: str, confidence_weight: float = 1.0):
        """Add a sensor to the fusion system"""
        self.sensors[sensor_id] = {
            'type': sensor_type,
            'status': 'active',
            'last_reading': None,
            'reliability_score': 1.0
        }
        self.confidence_weights[sensor_id] = confidence_weight
        
    def fuse_sensor_data(self, sensor_readings: Dict[str, Any]) -> Dict[str, Any]:
        """Fuse data from multiple sensors for better accuracy"""
        fused_result = {}
        
        # Environment monitoring example
        if 'temperature_sensors' in sensor_readings:
            temp_readings = sensor_readings['temperature_sensors']
            weights = [self.confidence_weights.get(sid, 1.0) for sid in temp_readings.keys()]
            values = list(temp_readings.values())
            
            # Weighted average with outlier detection
            fused_temp = np.average(values, weights=weights)
            
            # Detect and handle outliers
            outliers = [abs(v - fused_temp) > 2 * np.std(values) for v in values]
            if any(outliers):
                # Re-calculate without outliers
                clean_values = [v for v, is_outlier in zip(values, outliers) if not is_outlier]
                fused_temp = np.mean(clean_values) if clean_values else fused_temp
                
            fused_result['temperature'] = {
                'value': fused_temp,
                'confidence': min(1.0, len(temp_readings) / 3),  # Higher confidence with more sensors
                'outliers_detected': any(outliers)
            }
            
        # Motion detection fusion
        if 'motion_sensors' in sensor_readings:
            motion_data = sensor_readings['motion_sensors']
            motion_votes = sum(1 for reading in motion_data.values() if reading.get('motion_detected', False))
            total_sensors = len(motion_data)
            
            fused_result['motion'] = {
                'detected': motion_votes > total_sensors / 2,  # Majority vote
                'confidence': motion_votes / total_sensors,
                'supporting_sensors': motion_votes
            }
            
        # Environmental quality assessment
        if 'air_quality_sensors' in sensor_readings:
            air_data = sensor_readings['air_quality_sensors']
            
            # Combine multiple air quality metrics
            aqi_components = []
            for sensor_id, reading in air_data.items():
                if 'pm25' in reading:
                    aqi_components.append(self.pm25_to_aqi(reading['pm25']))
                if 'co2' in reading:
                    aqi_components.append(self.co2_to_score(reading['co2']))
                    
            fused_result['air_quality'] = {
                'aqi_score': max(aqi_components) if aqi_components else 0,
                'status': self.get_air_quality_status(max(aqi_components) if aqi_components else 0),
                'contributing_factors': len(aqi_components)
            }
            
        return fused_result
        
    def pm25_to_aqi(self, pm25_value: float) -> float:
        """Convert PM2.5 concentration to AQI score"""
        if pm25_value <= 12.0:
            return 50 * pm25_value / 12.0
        elif pm25_value <= 35.4:
            return 50 + 50 * (pm25_value - 12.0) / (35.4 - 12.0)
        elif pm25_value <= 55.4:
            return 100 + 50 * (pm25_value - 35.4) / (55.4 - 35.4)
        else:
            return min(300, 150 + 150 * (pm25_value - 55.4) / (150.4 - 55.4))
            
    def co2_to_score(self, co2_ppm: float) -> float:
        """Convert CO2 concentration to quality score"""
        if co2_ppm <= 400:
            return 0  # Excellent
        elif co2_ppm <= 1000:
            return 50 * (co2_ppm - 400) / 600
        elif co2_ppm <= 2000:
            return 50 + 50 * (co2_ppm - 1000) / 1000
        else:
            return min(150, 100 + 50 * (co2_ppm - 2000) / 3000)
            
    def get_air_quality_status(self, aqi_score: float) -> str:
        """Get human-readable air quality status"""
        if aqi_score <= 50:
            return "Good"
        elif aqi_score <= 100:
            return "Moderate"
        elif aqi_score <= 150:
            return "Unhealthy for Sensitive Groups"
        else:
            return "Unhealthy"

# Usage example
fusion_system = SensorFusionSystem()
fusion_system.add_sensor("temp_01", "temperature", 1.0)
fusion_system.add_sensor("temp_02", "temperature", 0.8)  # Less reliable
fusion_system.add_sensor("motion_01", "motion", 1.0)
fusion_system.add_sensor("air_01", "air_quality", 1.0)

sensor_readings = {
    'temperature_sensors': {
        'temp_01': 23.5,
        'temp_02': 28.0  # Outlier
    },
    'motion_sensors': {
        'motion_01': {'motion_detected': True},
        'motion_02': {'motion_detected': False}
    },
    'air_quality_sensors': {
        'air_01': {'pm25': 15.2, 'co2': 800}
    }
}

fused_data = fusion_system.fuse_sensor_data(sensor_readings)
print("Fused sensor data:", json.dumps(fused_data, indent=2))
\`\`\`

### 3. Distributed AI and Federated Learning

\`\`\`python
class FederatedIoTSystem:
    """Federated learning system for IoT devices"""
    
    def __init__(self):
        self.participants = {}
        self.global_model = None
        self.aggregation_weights = {}
        
    def register_participant(self, device_id: str, data_samples: int):
        """Register an IoT device for federated learning"""
        self.participants[device_id] = {
            'data_samples': data_samples,
            'local_model': None,
            'contribution_score': 0.0
        }
        
        # Weight based on data quantity and quality
        total_samples = sum(p['data_samples'] for p in self.participants.values())
        self.aggregation_weights[device_id] = data_samples / total_samples
        
    def local_training(self, device_id: str, local_data: np.ndarray, epochs: int = 5):
        """Simulate local training on IoT device"""
        print(f"Device {device_id} training locally with {len(local_data)} samples")
        
        # Simulate model training (in practice, this would be actual ML training)
        local_model_weights = np.random.randn(10, 5)  # Example model weights
        
        # Add some device-specific learning
        device_factor = hash(device_id) % 100 / 100.0
        local_model_weights += device_factor * 0.1
        
        self.participants[device_id]['local_model'] = local_model_weights
        return local_model_weights
        
    def federated_aggregation(self):
        """Aggregate local models using FedAvg algorithm"""
        if not any(p['local_model'] is not None for p in self.participants.values()):
            return None
            
        # Initialize aggregated model
        first_model = next(p['local_model'] for p in self.participants.values() 
                          if p['local_model'] is not None)
        aggregated_model = np.zeros_like(first_model)
        
        total_weight = 0
        for device_id, participant in self.participants.items():
            if participant['local_model'] is not None:
                weight = self.aggregation_weights[device_id]
                aggregated_model += weight * participant['local_model']
                total_weight += weight
                
        # Normalize
        if total_weight > 0:
            aggregated_model /= total_weight
            
        self.global_model = aggregated_model
        return aggregated_model
        
    def distribute_global_model(self):
        """Distribute updated global model to all participants"""
        if self.global_model is None:
            return False
            
        for device_id in self.participants.keys():
            print(f"Sending updated model to device {device_id}")
            # In practice, this would involve network communication
            
        return True
        
    def privacy_preserving_aggregation(self, noise_level: float = 0.1):
        """Add differential privacy to federated learning"""
        if self.global_model is None:
            return None
            
        # Add calibrated noise for differential privacy
        noise = np.random.laplace(0, noise_level, self.global_model.shape)
        private_model = self.global_model + noise
        
        return private_model

# Example usage
fed_system = FederatedIoTSystem()

# Register IoT devices
fed_system.register_participant("smart_thermostat_01", 1000)
fed_system.register_participant("security_camera_02", 1500)
fed_system.register_participant("motion_sensor_03", 800)

# Simulate federated learning round
for device_id in fed_system.participants.keys():
    # Generate some synthetic local data
    local_data = np.random.randn(fed_system.participants[device_id]['data_samples'], 5)
    fed_system.local_training(device_id, local_data)

# Aggregate models
global_model = fed_system.federated_aggregation()
print(f"Global model shape: {global_model.shape}")

# Distribute back to devices
fed_system.distribute_global_model()
\`\`\`

## Real-World Applications

### Smart City Infrastructure
- **Traffic optimization**: AI-powered traffic light control
- **Environmental monitoring**: Air quality and noise pollution
- **Energy management**: Smart grid optimization
- **Public safety**: Intelligent surveillance and emergency response

### Industrial IoT (IIoT)
- **Predictive maintenance**: Equipment failure prediction
- **Quality control**: Real-time defect detection
- **Supply chain optimization**: Intelligent logistics
- **Safety monitoring**: Worker safety and hazard detection

### Smart Healthcare
- **Remote patient monitoring**: Continuous health tracking
- **Drug adherence**: Smart pill dispensers
- **Emergency detection**: Fall detection and alerting
- **Environmental health**: Air quality impact on patients

## Challenges and Solutions

### Network Connectivity
- **Edge caching**: Store frequently used models locally
- **Intermittent connectivity**: Offline operation capabilities
- **Bandwidth optimization**: Compress model updates and data

### Security and Privacy
- **Encrypted communication**: Secure device-to-cloud channels
- **Differential privacy**: Privacy-preserving federated learning
- **Access control**: Device authentication and authorization
- **Secure boot**: Tamper-resistant edge devices

### Scalability
- **Device management**: Automated provisioning and updates
- **Load balancing**: Distribute compute across edge nodes
- **Hierarchical processing**: Multi-tier edge-cloud architecture
`,
    learningObjectives: [
      'Design edge-cloud hybrid IoT architectures',
      'Implement sensor fusion for multi-modal AI',
      'Apply federated learning to IoT systems',
      'Address security and privacy in smart IoT systems'
    ],
    quiz: {
      questions: [
        {
          id: 'smart-iot-1',
          question: 'What is the main advantage of edge computing in IoT systems?',
          options: [
            'Lower cost',
            'Reduced latency and bandwidth usage',
            'Better security',
            'Easier maintenance'
          ],
          correctAnswer: 1,
          explanation: 'Edge computing reduces latency by processing data locally and reduces bandwidth usage by only sending relevant information to the cloud.'
        },
        {
          id: 'smart-iot-2',
          question: 'How does sensor fusion improve IoT system reliability?',
          options: [
            'Reduces power consumption',
            'Combines multiple sensor inputs for better accuracy',
            'Simplifies system architecture',
            'Eliminates need for cloud processing'
          ],
          correctAnswer: 1,
          explanation: 'Sensor fusion combines data from multiple sensors to achieve better accuracy and reliability than any single sensor could provide.'
        },
        {
          id: 'smart-iot-3',
          question: 'What is federated learning in the context of IoT?',
          options: [
            'Centralized training on cloud servers',
            'Training ML models across distributed devices without sharing raw data',
            'Sharing trained models between devices',
            'Cloud-based model deployment'
          ],
          correctAnswer: 1,
          explanation: 'Federated learning allows training machine learning models across distributed IoT devices while keeping the raw data local, preserving privacy.'
        },
        {
          id: 'smart-iot-4',
          question: 'Which technique helps preserve privacy in IoT federated learning?',
          options: [
            'Data encryption only',
            'Differential privacy',
            'Access control',
            'Secure communication protocols'
          ],
          correctAnswer: 1,
          explanation: 'Differential privacy adds calibrated noise to model updates, providing mathematical guarantees about privacy protection in federated learning.'
        },
        {
          id: 'smart-iot-5',
          question: 'What is a key challenge in smart city IoT deployments?',
          options: [
            'High computational requirements',
            'Scalability and network connectivity',
            'Limited sensor types',
            'Lack of applications'
          ],
          correctAnswer: 1,
          explanation: 'Smart city IoT deployments face challenges in scalability (managing thousands of devices) and ensuring reliable network connectivity across diverse urban environments.'
        }
      ]
    }
  }
];